import { DragEventHandler, RefObject, useCallback, useState } from "react";

import { usePlayContext } from "../../../pages/Play/play-context";
import { useGetUserPlaySettingsQuery } from "../../../redux/slices";
import { Action, ActionType, StencilType } from "../../../types";
import { createStencilFromToolPalette } from "../../../utils";
import { SelectActionMenu } from "../../select-action-menu";
import { PALETTE_DRAG_ID } from "../../tool-palette/tool-palette";
import { getSurfacePosition } from "../utils";

export interface UseOnDropProps {
  surfaceContainerRef: RefObject<HTMLDivElement>;
  svgRef: RefObject<SVGSVGElement>;
  adjustXUnits: number;
  adjustYUnits: number;
  surfaceWidthUnits: number;
  surfaceHeightUnits: number;
  openTooltip: (menu: SelectActionMenu) => void;
}

export const useOnDrop = ({
  surfaceContainerRef,
  svgRef,
  adjustXUnits,
  adjustYUnits,
  surfaceWidthUnits,
  surfaceHeightUnits,
  openTooltip,
}: UseOnDropProps) => {
  const { data: userPlaySettings } = useGetUserPlaySettingsQuery();
  const [previewAction, setPreviewAction] = useState<Action | undefined>(undefined);

  const { frameStencilsApi, toolPaletteApi, playStateApi } = usePlayContext();
  const { playState } = playStateApi;
  const { sport, orientation } = playState;
  const { positionLabelType } = toolPaletteApi;
  const { addStencil } = frameStencilsApi;

  const onDrop: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const surfaceContainerElement = surfaceContainerRef.current;
      const svgElement = svgRef.current;

      if (surfaceContainerElement && svgElement) {
        const id = event.dataTransfer.getData(PALETTE_DRAG_ID);

        const { nativeEvent } = event;
        const { offsetX, offsetY } = nativeEvent;
        const { x, y } = getSurfacePosition({
          event,
          offsetX,
          offsetY,
          svgElement,
          surfaceContainerElement,
          adjustXUnits,
          adjustYUnits,
          surfaceWidthUnits,
          surfaceHeightUnits,
          orientation,
        });

        const newStencil = createStencilFromToolPalette({ id, positionLabelType, pos: { x, y }, sport });

        if (newStencil) {
          // If ENB Act Palette is enabled and we're dropping an action show the variation tooltip
          if (
            userPlaySettings?.enbActPalette &&
            newStencil.stencilType === StencilType.ACTION &&
            newStencil.actionType !== ActionType.HANDOFF
          ) {
            setPreviewAction(newStencil);
            openTooltip({
              menuPos: { x: event.pageX + 50, y: event.pageY - 200 },
              action: newStencil,
              cleanup: () => setPreviewAction(undefined),
            });
          } else {
            addStencil({ stencil: newStencil });
          }
        }
      }
    },
    [
      surfaceContainerRef,
      svgRef,
      adjustXUnits,
      adjustYUnits,
      surfaceWidthUnits,
      surfaceHeightUnits,
      positionLabelType,
      userPlaySettings?.enbActPalette,
      openTooltip,
      addStencil,
      sport,
      orientation,
    ]
  );

  return { onDrop, previewAction };
};
